import clsx from 'clsx';
import { Ban, Check, CircleAlert } from 'lucide-react';
import React from 'react';
import type { Market } from '@//config/domain-locale-map';
import { LicensePlateIdentifier } from '../LicensePlateIdentifier';
import styles from './Input.module.scss';

export type InputState = 'success' | 'error';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
  unit?: string;
  unitPosition?: 'start' | 'end';
  suffix?: string;
  state?: InputState;
  disabled?: boolean;
  licensePlateBadge?: boolean;
  market?: Market;
  testId?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      placeholder,
      ariaLabel,
      ariaDescribedby,
      unit,
      unitPosition = 'end',
      suffix,
      state,
      disabled,
      licensePlateBadge,
      market = 'wayke',
      testId,
      ...props
    },
    ref,
  ) => {
    const getStateIcon = (state: string) => {
      switch (state) {
        case 'success':
          return <Check />;
        case 'error':
          return <CircleAlert />;
        case 'disabled':
          return <Ban />;
        default:
          return null;
      }
    };

    return (
      <div
        className={clsx(styles.wrapper, {
          [styles.success]: state === 'success',
          [styles.error]: state === 'error',
        })}
      >
        <input
          ref={ref}
          type={props.type || 'text'}
          id={id}
          className={styles.input}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          disabled={disabled}
          data-testid={testId}
          {...props}
        />
        {unit && (
          <span
            className={clsx(styles.unit, {
              [styles.unitStart]: unitPosition === 'start',
            })}
            data-testid={testId ? `${testId}-unit` : undefined}
          >
            {unit}
          </span>
        )}
        {suffix && (
          <span className={styles.suffix} data-testid={testId ? `${testId}-suffix` : undefined}>
            {suffix}
          </span>
        )}
        {licensePlateBadge && (
          <LicensePlateIdentifier
            market={market}
            className={styles.licensePlateBadge}
            data-testid={testId ? `${testId}-license-plate-badge` : undefined}
          />
        )}
        {(state || disabled) && (
          <div
            className={styles.state}
            aria-hidden="true"
            data-testid={testId ? `${testId}-state` : undefined}
          >
            {getStateIcon(state || 'disabled')}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
